import React from 'react';
import { useTranslation } from 'react-i18next';
import { Flex, Form, InputNumber, Radio, Select, Typography } from 'antd';
import { getCargoTypeOptions } from 'shared/utils/cargos/get-cargo-type-options';
import { CargoCustomRequirements, CargoType } from 'types/cargo.ts';
import type { ICargoFormProps } from 'components/cargos/cargo-form/types';
import { CargoItemInputFieldsSection } from 'components/common/cargo-item-input-fields-section';
import { ContainerCargoItemInputFields } from 'components/common/container-cargo-item-input-fields';
import styles from './cargo-form.module.scss';
import { CargoAddressFormBlock } from './components/cargo-address-form-block';
import { CargoTypeLdmAlert } from './components/cargo-type-ldm-alert';
import { validationRule } from './utils';

export const CargoForm: React.FC<ICargoFormProps> = (props) => {
  const { form, formId, onFinish, initialValues, withoutItems } = props;
  const { t } = useTranslation();

  const loadingCustom = Form.useWatch('loadingCustom', form);
  const unloadingCustom = Form.useWatch('unloadingCustom', form);
  const cargoType = Form.useWatch('type', form) ?? initialValues?.type ?? CargoType.LTL;

  const cargoTypeOptions = getCargoTypeOptions();

  return (
    <Form
      form={form}
      id={formId}
      layout='vertical'
      className={styles.form}
      initialValues={initialValues}
      onFinish={onFinish}>
      <Typography.Text className={styles.sectionTitle} strong>
        {t('cargo:loading_info')}
      </Typography.Text>
      <CargoAddressFormBlock form={form} prefix='loadingAddress' dateInputName='loadingDate' withAdditionalInfo />

      <Typography.Text className={styles.sectionTitle} strong>
        {t('cargo:loading_custom_info')}
      </Typography.Text>
      <div className={styles.mdMarginBottom}>
        <Form.Item
          name='loadingCustom'
          rootClassName={styles.marginNone}
          initialValue={CargoCustomRequirements.need_address}>
          <Radio.Group>
            <Radio value={CargoCustomRequirements.need_address}>{t('cargo:with_custom')}</Radio>
            <Radio value={CargoCustomRequirements.without_custom}>{t('cargo:without_custom')}</Radio>
            <Radio value={CargoCustomRequirements.custom_in_place}>{t('cargo:custom_in_place')}</Radio>
            <Radio value={CargoCustomRequirements.our_custom_clearance}>{t('cargo:our_custom_clearance')}</Radio>
          </Radio.Group>
        </Form.Item>
      </div>
      {loadingCustom === CargoCustomRequirements.need_address ? (
        <CargoAddressFormBlock form={form} prefix='loadingCustomAddress' />
      ) : null}

      <Typography.Text className={styles.sectionTitle} strong>
        {t('cargo:unloading_info')}
      </Typography.Text>
      <CargoAddressFormBlock form={form} prefix='unloadingAddress' dateInputName='unloadingDate' withAdditionalInfo />

      <Typography.Text className={styles.sectionTitle} strong>
        {t('cargo:unloading_custom_info')}
      </Typography.Text>
      <div className={styles.mdMarginBottom}>
        <Form.Item
          name='unloadingCustom'
          rootClassName={styles.marginNone}
          initialValue={CargoCustomRequirements.need_address}>
          <Radio.Group>
            <Radio value={CargoCustomRequirements.need_address}>{t('cargo:with_custom')}</Radio>
            <Radio value={CargoCustomRequirements.without_custom}>{t('cargo:without_custom')}</Radio>
            <Radio value={CargoCustomRequirements.custom_in_place}>{t('cargo:custom_in_place')}</Radio>
            <Radio value={CargoCustomRequirements.our_custom_clearance}>{t('cargo:our_custom_clearance')}</Radio>
          </Radio.Group>
        </Form.Item>
      </div>
      {unloadingCustom === CargoCustomRequirements.need_address ? (
        <CargoAddressFormBlock form={form} prefix='unloadingCustomAddress' />
      ) : null}

      <Flex gap='middle'>
        <Form.Item name='totalWeight' rules={validationRule.totalWeight} label={t('cargo:total_weight_label')}>
          <InputNumber decimalSeparator={','} style={{ minWidth: '150px' }} />
        </Form.Item>
        <Form.Item name='type' initialValue={CargoType.LTL} rules={validationRule.type} label={t('cargo:cargo_type')}>
          <Select
            optionFilterProp='label'
            placeholder={t('cargo:cargo_type')}
            style={{ minWidth: '150px' }}
            options={cargoTypeOptions}
            defaultValue={CargoType.LTL}
            showSearch
          />
        </Form.Item>
      </Flex>

      {cargoType === CargoType.CONTAINER ? (
        <>
          <Typography.Text className={styles.sectionTitle} strong>
            {t('cargo:return_address')}
          </Typography.Text>
          <CargoAddressFormBlock form={form} prefix='assetReturnAddress' />
        </>
      ) : null}

      {!withoutItems ? <CargoTypeLdmAlert form={form} /> : null}

      {!withoutItems ? (
        <>
          <Typography.Text className={styles.sectionTitle} strong>
            {t('cargo:cargo_items')}
          </Typography.Text>
          {cargoType === CargoType.CONTAINER ? (
            <ContainerCargoItemInputFields name='specificCargoItem' form={form} />
          ) : (
            <CargoItemInputFieldsSection
              name='cargoItems'
              form={form}
              initialItemCount={initialValues?.cargoItems?.length}
            />
          )}
        </>
      ) : null}
    </Form>
  );
};
